import { createRoute, lazyRouteComponent, useParams, type AnyRoute } from "@tanstack/react-router";
import { lazy } from "react";

/**
 * Routes of tasks 16.5-16.8: device profile, transaction detail, blocklist and overview map. They
 * hang under the signed-in shell (no environment parameter: the installation has one) and load lazily,
 * so the map library stays out of the main bundle.
 */
export function createDeviceBlocksMapRoutes<Parent extends AnyRoute>(parent: Parent) {
  const deviceRoute = createRoute({
    getParentRoute: () => parent,
    path: "/devices/$ref",
    component: function DeviceRoute() {
      const params: Record<string, string | undefined> = useParams({ strict: false });
      return <DeviceProfile deviceRef={params.ref ?? ""} />;
    },
  });
  const transactionRoute = createRoute({
    getParentRoute: () => parent,
    path: "/transactions/$id",
    component: function TransactionRoute() {
      const params: Record<string, string | undefined> = useParams({ strict: false });
      return <TransactionDetail transactionId={params.id ?? ""} />;
    },
  });
  const blocklistRoute = createRoute({
    getParentRoute: () => parent,
    path: "/blocklist",
    component: lazyRouteComponent(
      () => import("../features/blocklist/containers/blocks-page.tsx"),
      "BlocksPage",
    ),
  });
  const mapRoute = createRoute({
    getParentRoute: () => parent,
    path: "/map",
    component: lazyRouteComponent(
      () => import("../features/overview-map/containers/overview-map-page.tsx"),
      "OverviewMapPage",
    ),
  });
  return [deviceRoute, transactionRoute, blocklistRoute, mapRoute] as const;
}

const DeviceProfile = lazy(() =>
  import("../features/device-profile/containers/device-profile-page.tsx").then((m) => ({
    default: m.DeviceProfilePage,
  })),
);
const TransactionDetail = lazy(() =>
  import("../features/transaction-detail/containers/transaction-detail-page.tsx").then((m) => ({
    default: m.TransactionDetailPage,
  })),
);
